import { type HttpErrorCode, type HttpErrorResponse, type HttpRequestConfig } from '../types/http';

type HttpErrorInit<T = unknown> = {
  cause?: unknown;
  code?: HttpErrorCode;
  request?: HttpRequestConfig;
  response?: HttpErrorResponse<T>;
};

export class HttpError<T = unknown> extends Error {
  readonly code?: HttpErrorCode;
  readonly isHttpError = true;
  readonly request?: HttpRequestConfig;
  readonly response?: HttpErrorResponse<T>;
  readonly status?: number;
  readonly statusText?: string;
  readonly cause?: unknown;

  constructor(message: string, init: HttpErrorInit<T> = {}) {
    super(message);
    this.name = 'HttpError';
    this.code = init.code;
    this.request = init.request;
    this.response = init.response;
    this.status = init.response?.status;
    this.statusText = init.response?.statusText;
    this.cause = init.cause;
  }

  static fromResponse<T = unknown>(
    response: Response,
    init: {
      code?: HttpErrorCode;
      data?: T;
      message?: string;
      request?: HttpRequestConfig;
    } = {},
  ) {
    return new HttpError<T>(init.message ?? `Request failed with status code ${response.status}`, {
      code: init.code ?? getHttpErrorCode(response.status),
      request: init.request,
      response: {
        data: init.data,
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      },
    });
  }
}

export const getHttpErrorCode = (status: number): HttpErrorCode => {
  return status >= 500 ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST';
};

export const isHttpError = (error: unknown): error is HttpError => {
  return error instanceof HttpError || (error instanceof Error && 'isHttpError' in error && error.isHttpError === true);
};
